import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Building2, Cpu, FileSearch, Search, X, Wrench, Truck, Package,
  ChevronRight, MapPin, Factory, Award, Tag, Calendar, Loader2, TrendingUp,
} from "lucide-react";
import SideFilters from "../../components/SideFilters";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_COLOR = {
  Operativo: "#16C784",
  Ottimo: "#16C784",
  Manutenzione: "#F59E0B",
  "Da sostituire": "#DC2626",
};
const PATENT_COLOR = {
  Attivo: "#16C784",
  "In registrazione": "#F59E0B",
  Scaduto: "#DC2626",
};

function scoreColor(s) {
  if (s >= 75) return "#16C784";
  if (s >= 50) return "#F59E0B";
  return "#DC2626";
}

// ══════════════════════════════════════════════════════════════════
// Detail Modal
// ══════════════════════════════════════════════════════════════════
function CompanyDetailModal({ companyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/tech/monitor/company/${companyId}`, { withCredentials: true });
        setData(data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, [companyId]);

  if (!companyId) return null;

  const TABS = [
    { id: "overview", label: "Overview", icon: Building2 },
    { id: "technologies", label: "Tecnologie", icon: Cpu, count: data?.technologies?.length },
    { id: "patents", label: "Brevetti", icon: FileSearch, count: data?.patents?.length },
    { id: "cnc", label: "Macchine CNC", icon: Factory, count: data?.cnc_machines?.length },
    { id: "machinery", label: "Macchinari", icon: Wrench, count: data?.machinery?.length },
    { id: "fleet", label: "Flotta veicoli", icon: Truck, count: data?.vehicle_fleet?.length },
    { id: "other", label: "Altri asset", icon: Package, count: data?.other_equipment?.length },
  ];

  return (
    <div data-testid="rd-company-modal" className="fixed inset-0 flex z-[9999] animate-fadeIn" onClick={onClose}>
      <div className="flex-1 detail-backdrop-solid" />
      <div onClick={(e) => e.stopPropagation()}
        className="detail-panel-light h-full w-full max-w-[960px] flex flex-col overflow-hidden shadow-[0_0_60px_rgba(0,229,255,0.15)] animate-slideInRight">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-[#00E5FF]/10 flex-shrink-0">
            <Factory className="w-5 h-5 text-[#00E5FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
              {data?.company?.name || "..."}
            </h2>
            <div className="flex items-center gap-3 mt-1 text-xs text-[#CBD5E1] flex-wrap">
              {data?.company?.sector && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{data.company.sector}</span>}
              {(data?.company?.hq || data?.company?.location) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{data.company.hq || data.company.location}</span>}
              {data?.company?.employees && <span className="text-[#A0AEC0]">{data.company.employees} dipendenti</span>}
            </div>
          </div>
          <button data-testid="rd-modal-close" onClick={onClose}
            className="text-[#CBD5E1] hover:text-white hover:bg-[#1F242D] p-2 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 border-b border-white/[0.04] bg-[#0D0F12] overflow-x-auto">
          {TABS.map(T => (
            <button
              key={T.id}
              data-testid={`rd-tab-${T.id}`}
              onClick={() => setTab(T.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-[11px] tracking-widest uppercase font-semibold border-b-2 whitespace-nowrap transition-colors ${
                tab === T.id ? "border-[#00E5FF] text-[#00E5FF]" : "border-transparent text-[#A0AEC0] hover:text-white"
              }`}
            >
              <T.icon className="w-3.5 h-3.5" />
              {T.label}
              {T.count !== undefined && T.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-sm text-[9px] ${tab === T.id ? "bg-[#00E5FF] text-black" : "bg-[#1F242D] text-[#A0AEC0]"}`}>{T.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#A0AEC0]">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
            </div>
          ) : !data ? (
            <p className="text-sm text-[#A0AEC0]">Dati non disponibili.</p>
          ) : (
            <>
              {tab === "overview" && <OverviewTab data={data} />}
              {tab === "technologies" && <TechnologiesTab items={data.technologies} />}
              {tab === "patents" && <PatentsTab items={data.patents} />}
              {tab === "cnc" && <EquipmentTab items={data.cnc_machines} emptyLabel="Nessuna macchina CNC registrata" />}
              {tab === "machinery" && <EquipmentTab items={data.machinery} emptyLabel="Nessun macchinario registrato" />}
              {tab === "fleet" && <EquipmentTab items={data.vehicle_fleet} emptyLabel="Nessun veicolo in flotta" />}
              {tab === "other" && <EquipmentTab items={data.other_equipment} emptyLabel="Nessun altro asset registrato" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ data }) {
  const kpis = [
    { label: "Tecnologie", value: data.technologies.length, icon: Cpu, color: "#00E5FF" },
    { label: "Brevetti", value: data.patents.length, icon: FileSearch, color: "#A78BFA" },
    { label: "Macchine CNC", value: data.cnc_machines.length, icon: Factory, color: "#F59E0B" },
    { label: "Macchinari", value: data.machinery.length, icon: Wrench, color: "#059669" },
    { label: "Veicoli flotta", value: data.vehicle_fleet.length, icon: Truck, color: "#DC2626" },
    { label: "Altri asset", value: data.other_equipment.length, icon: Package, color: "#7C3AED" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {kpis.map(k => (
        <div key={k.label} className="bg-[#0D0F12] border border-white/[0.04] p-4">
          <div className="flex items-start justify-between mb-2">
            <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0] font-semibold">{k.label}</p>
            <k.icon className="w-3.5 h-3.5" style={{ color: k.color }} />
          </div>
          <p className="text-2xl font-bold tabular-nums" style={{ color: k.color, fontFamily: "Chivo, sans-serif" }}>{k.value}</p>
        </div>
      ))}
    </div>
  );
}

function TechnologiesTab({ items }) {
  if (!items?.length) return <p className="text-sm text-[#A0AEC0] italic">Nessuna tecnologia registrata.</p>;
  const bySection = { software: [], hardware: [] };
  items.forEach(t => bySection[t.category === "hardware" ? "hardware" : "software"].push(t));
  return (
    <div className="space-y-6">
      {["software", "hardware"].map(sec => bySection[sec].length > 0 && (
        <div key={sec}>
          <p className="text-[11px] tracking-widest uppercase text-[#00E5FF] font-bold mb-3 flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5" /> {sec === "software" ? "Software Stack" : "Hardware Stack"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {bySection[sec].map((t, i) => (
              <div key={`${t.name}-${i}`} className="bg-[#0D0F12] border border-white/[0.04] p-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium truncate">{t.name}</p>
                  <p className="text-[10px] text-[#A0AEC0] mt-0.5">{t.type} · dal {t.intro_year}</p>
                </div>
                <span className="text-sm font-bold tabular-nums flex-shrink-0" style={{ color: scoreColor(t.score) }}>{t.score}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PatentsTab({ items }) {
  const [selected, setSelected] = useState(null);
  if (!items?.length) return <p className="text-sm text-[#A0AEC0] italic">Nessun brevetto registrato.</p>;
  return (
    <>
      <div className="space-y-3">
        {items.map(p => (
          <button
            key={p.id}
            data-testid="patent-card"
            onClick={() => setSelected(p)}
            className="w-full text-left bg-[#0D0F12] border border-white/[0.04] p-4 hover:border-[#00E5FF] hover:bg-[#1A1D24] transition-colors group"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-semibold group-hover:text-[#00E5FF]" title={p.title}>{p.title}</p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[10px] text-[#A0AEC0]">
                  <span className="font-mono">{p.patent_number}</span>
                  {p.filing_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.filing_date}</span>}
                  {p.ipc_class && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />IPC {p.ipc_class}</span>}
                  {p.cpc_class && <span>CPC {p.cpc_class}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] px-2 py-0.5 border tracking-widest uppercase font-bold"
                  style={{ color: PATENT_COLOR[p.status] || "#00E5FF", borderColor: (PATENT_COLOR[p.status] || "#00E5FF") + "66", background: (PATENT_COLOR[p.status] || "#00E5FF") + "15" }}>
                  {p.status}
                </span>
                <ChevronRight className="w-4 h-4 text-[#A0AEC0] group-hover:text-[#00E5FF]" />
              </div>
            </div>
            {p.abstract && <p className="text-xs text-[#CBD5E1] mt-3 leading-relaxed line-clamp-2">{p.abstract}</p>}
          </button>
        ))}
      </div>
      <PatentDetailModal patent={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function PatentDetailModal({ patent, onClose }) {
  if (!patent) return null;
  const statusColor = PATENT_COLOR[patent.status] || "#00E5FF";
  // A patent is really a "marchio" (trademark) when kind field is set — support both
  const kind = patent.kind || (patent.title || "").toLowerCase().includes("marchio") ? "trademark" : "patent";
  const isPatent = kind !== "trademark";
  const KIND_LABEL = isPatent ? "Brevetto" : "Marchio";

  return (
    <div data-testid="patent-detail-modal" className="fixed inset-0 flex z-[10000]" onClick={onClose}>
      <div className="flex-1 detail-backdrop-solid" />
      <div onClick={(e) => e.stopPropagation()}
        className="detail-panel-light h-full w-full max-w-[720px] border-l-2 border-[#A78BFA] flex flex-col overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-12 h-12 flex items-center justify-center border-2 border-[#A78BFA] bg-[#A78BFA]/10 flex-shrink-0">
            <FileSearch className="w-6 h-6 text-[#A78BFA]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] tracking-widest uppercase text-[#A78BFA] font-bold">{KIND_LABEL}</span>
              <span
                className="text-[10px] px-2 py-0.5 border tracking-widest uppercase font-bold"
                style={{ color: statusColor, borderColor: `${statusColor}66`, background: `${statusColor}15` }}
              >
                {patent.status}
              </span>
              {patent.patent_number && <span className="text-[10px] text-[#A0AEC0] font-mono">{patent.patent_number}</span>}
            </div>
            <h2 className="text-lg font-bold text-white mt-1 leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              {patent.title}
            </h2>
            {patent.company && <p className="text-xs text-[#CBD5E1] mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />{patent.company}</p>}
          </div>
          <button data-testid="patent-modal-close" onClick={onClose}
            className="text-[#CBD5E1] hover:text-white hover:bg-[#1F242D] p-2 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Info blocks */}
          <section>
            <p className="text-[11px] text-[#A78BFA] tracking-widest uppercase font-bold mb-3">Dati principali</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["Numero registrazione", patent.patent_number],
                ["Data deposito",         patent.filing_date],
                ["Data pubblicazione",    patent.publication_date],
                ["Data concessione",      patent.grant_date],
                ["Data scadenza",         patent.expiry_date],
                ["Ufficio",               patent.office || patent.authority || "EPO / UIBM"],
                ["Classificazione IPC",   patent.ipc_class],
                ["Classificazione CPC",   patent.cpc_class],
                ["Classe di Nizza",       patent.nice_class],
                ["Territorio",            patent.territory || (patent.country ? patent.country : null)],
                ["Priorità",              patent.priority || patent.priority_date],
                ["Famiglia brevettuale",  patent.family_size ? `${patent.family_size} membri` : null],
              ].filter(([, v]) => v !== undefined && v !== null && v !== "").map(([l, v]) => (
                <div key={l} className="bg-[#0D0F12] border border-white/[0.04] p-3">
                  <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{l}</p>
                  <p className="text-xs text-white font-medium font-mono">{v}</p>
                </div>
              ))}
            </div>
          </section>

          {patent.abstract && (
            <section>
              <p className="text-[11px] text-[#A78BFA] tracking-widest uppercase font-bold mb-2">Abstract</p>
              <div className="bg-[#0D0F12] border border-white/[0.04] p-4">
                <p className="text-sm text-[#D1D5DB] leading-relaxed">{patent.abstract}</p>
              </div>
            </section>
          )}

          {patent.claims && (
            <section>
              <p className="text-[11px] text-[#A78BFA] tracking-widest uppercase font-bold mb-2">Rivendicazioni principali</p>
              <div className="bg-[#0D0F12] border border-white/[0.04] p-4">
                <p className="text-sm text-[#D1D5DB] leading-relaxed whitespace-pre-wrap">{patent.claims}</p>
              </div>
            </section>
          )}

          {patent.inventors?.length > 0 && (
            <section>
              <p className="text-[11px] text-[#A78BFA] tracking-widest uppercase font-bold mb-2">Inventori</p>
              <div className="flex flex-wrap gap-2">
                {patent.inventors.map((inv, i) => (
                  <span key={`${inv}-${i}`} className="text-xs px-2.5 py-1 bg-[#1A1D24] border border-white/[0.04] text-[#CBD5E1] rounded-sm">
                    {inv}
                  </span>
                ))}
              </div>
            </section>
          )}

          {patent.applicants?.length > 0 && (
            <section>
              <p className="text-[11px] text-[#A78BFA] tracking-widest uppercase font-bold mb-2">Titolari</p>
              <div className="flex flex-wrap gap-2">
                {patent.applicants.map((a, i) => (
                  <span key={`${a}-${i}`} className="text-xs px-2.5 py-1 bg-[#1A1D24] border border-white/[0.04] text-white rounded-sm">
                    {a}
                  </span>
                ))}
              </div>
            </section>
          )}

          {patent.link && (
            <a
              href={patent.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#A78BFA] hover:underline text-xs"
            >
              Apri documento originale <ChevronRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function EquipmentTab({ items, emptyLabel }) {
  if (!items?.length) return <p className="text-sm text-[#A0AEC0] italic">{emptyLabel}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
            {["Nome", "Modello", "Brand", "Anno", "Quantità", "Stato", "Note"].map(h => (
              <th key={h} className="text-left px-3 py-2.5 font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(e => (
            <tr key={e.id} className="border-b border-white/[0.04] hover:bg-[#1A1D24] transition-colors">
              <td className="px-3 py-2.5 text-white font-medium">{e.name}</td>
              <td className="px-3 py-2.5 text-[#CBD5E1]">{e.model || "—"}</td>
              <td className="px-3 py-2.5 text-[#CBD5E1]">{e.brand || "—"}</td>
              <td className="px-3 py-2.5 text-[#CBD5E1] tabular-nums">{e.year || "—"}</td>
              <td className="px-3 py-2.5 tabular-nums text-[#00E5FF] font-bold">{e.quantity || 1}×</td>
              <td className="px-3 py-2.5">
                <span className="text-[10px] px-2 py-0.5 border tracking-widest uppercase font-bold whitespace-nowrap"
                  style={{ color: STATUS_COLOR[e.status] || "#00E5FF", borderColor: (STATUS_COLOR[e.status] || "#00E5FF") + "66", background: (STATUS_COLOR[e.status] || "#00E5FF") + "15" }}>
                  {e.status}
                </span>
              </td>
              <td className="px-3 py-2.5 text-[#A0AEC0] text-[10px]">{e.notes || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════
export default function RDTechnologiesPage() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tech/monitor/companies`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fSectors = filters.sector || [];
    const fCountry = (filters.country || "").trim().toLowerCase();
    const hasPat = filters.has_patents === true;
    const hasCnc = filters.has_cnc === true;
    const hasFleet = filters.has_fleet === true;
    const hasMach = filters.has_machinery === true;
    return items.filter(c => {
      if (q && ![c.name, c.sector, c.hq].some(v => (v || "").toLowerCase().includes(q))) return false;
      if (fSectors.length && !fSectors.includes(c.sector)) return false;
      if (fCountry && !(c.hq || "").toLowerCase().includes(fCountry)) return false;
      if (hasPat && c.patent_count === 0) return false;
      if (hasCnc && c.cnc_count === 0) return false;
      if (hasFleet && c.fleet_count === 0) return false;
      if (hasMach && c.machinery_count === 0) return false;
      return true;
    });
  }, [items, search, filters]);

  const facetSections = useMemo(() => {
    const sectorMap = new Map();
    items.forEach(c => { if (c.sector) sectorMap.set(c.sector, (sectorMap.get(c.sector) || 0) + 1); });
    const sectorOpts = Array.from(sectorMap.entries()).sort((a, b) => b[1] - a[1]).slice(0, 25)
      .map(([s, n]) => ({ value: s, label: s, count: n }));
    return [
      { id: "sector",    label: "Settore",       icon: Factory,  type: "checkbox", options: sectorOpts },
      { id: "country",   label: "Sede",          icon: MapPin,   type: "text",     placeholder: "es. Milano, IT…" },
      { id: "has_patents",   label: "Con brevetti",    icon: FileSearch, type: "toggle", toggleLabel: "Solo aziende con brevetti" },
      { id: "has_cnc",       label: "Con CNC",         icon: Factory,    type: "toggle", toggleLabel: "Solo aziende con macchine CNC" },
      { id: "has_machinery", label: "Con macchinari",  icon: Wrench,     type: "toggle", toggleLabel: "Solo aziende con macchinari" },
      { id: "has_fleet",     label: "Con flotta",      icon: Truck,      type: "toggle", toggleLabel: "Solo aziende con flotta veicoli" },
    ];
  }, [items]);

  // KPIs sopra
  const totals = useMemo(() => filtered.reduce((s, c) => ({
    companies: s.companies + 1,
    tech: s.tech + c.tech_count,
    patents: s.patents + c.patent_count,
    cnc: s.cnc + c.cnc_count,
    machinery: s.machinery + c.machinery_count,
    fleet: s.fleet + c.fleet_count,
  }), { companies: 0, tech: 0, patents: 0, cnc: 0, machinery: 0, fleet: 0 }), [filtered]);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            {t("menu.tech")}
          </h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">
            {items.length} aziende · tecnologie, brevetti e asset produttivi
          </p>
        </div>
      </div>

      {/* KPI banner */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          { label: "Aziende", value: totals.companies, icon: Building2, color: "#00E5FF" },
          { label: "Tecnologie", value: totals.tech, icon: Cpu, color: "#0891B2" },
          { label: "Brevetti", value: totals.patents, icon: FileSearch, color: "#A78BFA" },
          { label: "Macchine CNC", value: totals.cnc, icon: Factory, color: "#F59E0B" },
          { label: "Macchinari", value: totals.machinery, icon: Wrench, color: "#059669" },
          { label: "Veicoli", value: totals.fleet, icon: Truck, color: "#DC2626" },
        ].map(k => (
          <div key={k.label} className="bg-[#0D0F12] border border-white/[0.04] px-3 py-2.5 rounded-md">
            <div className="flex items-center justify-between mb-0.5">
              <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0] font-semibold">{k.label}</p>
              <k.icon className="w-3 h-3" style={{ color: k.color }} />
            </div>
            <p className="text-lg font-bold tabular-nums leading-none mt-1" style={{ color: k.color, fontFamily: "Chivo, sans-serif" }}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        <SideFilters
          open={filtersOpen} setOpen={setFiltersOpen}
          filters={filters} setFilters={setFilters}
          sections={facetSections}
          totalMatch={filtered.length} totalAll={items.length}
          entity="rd-tech"
        />

        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <input
              data-testid="rd-tech-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filtra per azienda, settore, sede…"
              className="w-full bg-[#0D0F12] border border-white/[0.04] text-white pl-10 pr-10 py-2 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#6B7280] rounded-md"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="bg-[#0D0F12] border border-white/[0.04] rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
                    {["Azienda", "Settore", "Sede", "Tech", "Score", "Brevetti", "CNC", "Macchinari", "Flotta", ""].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="text-center py-8 text-[#A0AEC0]">Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={10} className="text-center py-8 text-[#A0AEC0]">Nessuna azienda corrisponde ai filtri.</td></tr>
                  ) : filtered.map(c => (
                    <tr
                      key={c.id || c.name}
                      data-testid="rd-company-row"
                      onClick={() => c.id && setSelectedId(c.id)}
                      className={`border-b border-white/[0.04] transition-colors ${c.id ? "hover:bg-[#1A1D24] cursor-pointer" : ""}`}
                    >
                      <td className="px-3 py-2.5 text-white font-medium">{c.name}</td>
                      <td className="px-3 py-2.5 text-[#CBD5E1]">{c.sector || "—"}</td>
                      <td className="px-3 py-2.5 text-[#CBD5E1]">{c.hq || "—"}</td>
                      <td className="px-3 py-2.5 tabular-nums text-[#00E5FF] font-bold">{c.tech_count}</td>
                      <td className="px-3 py-2.5 tabular-nums font-bold" style={{ color: scoreColor(c.tech_score || 0) }}>
                        {c.tech_score || "—"}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-[#A78BFA] font-bold">
                        {c.patent_count} {c.patent_active > 0 && <span className="text-[9px] text-[#16C784]">({c.patent_active} attivi)</span>}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-[#F59E0B] font-bold">{c.cnc_count}</td>
                      <td className="px-3 py-2.5 tabular-nums text-[#059669] font-bold">{c.machinery_count}</td>
                      <td className="px-3 py-2.5 tabular-nums text-[#DC2626] font-bold">{c.fleet_count}</td>
                      <td className="px-3 py-2.5">
                        {c.id && <ChevronRight className="w-4 h-4 text-[#A0AEC0]" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CompanyDetailModal companyId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
